import styled from "@emotion/styled"
import {
	PButtonPure,
	PHeading,
	PTable,
	PTableBody,
	PTableCell,
	PTableHead,
	PTableHeadCell,
	PTableHeadRow,
	PTableRow,
	PTag,
	PText,
} from "@porsche-design-system/components-react"
import { spacingStaticSm, spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import type { EquipmentOptionView } from "@slfinrtl/ui-bff-routes"
import { useState } from "react"
import { FormattedMessage } from "react-intl"
import { ButtonGroup } from "../../form/buttonGroup/ButtonGroup"
import { editableEquipmentListMessages } from "./editableEquipmentList.messages"

export interface EditableEquipmentListProps {
	equipmentList: EquipmentOptionView[]
	onAddCustomEquipment: () => void
	onRemove: (removedEquipment: EquipmentOptionView) => Promise<void> | void
	onResetToFactoryEquipment?: () => void
}

export const EditableEquipmentList = ({
	equipmentList,
	onAddCustomEquipment,
	onRemove,
	onResetToFactoryEquipment,
}: EditableEquipmentListProps) => {
	const [optionRemovalInProgress, setOptionRemovalInProgress] = useState<Record<string, boolean>>({})

	return (
		<div>
			<StyledHeading size="md">
				<FormattedMessage {...editableEquipmentListMessages.otherEquipmentTitle} />
			</StyledHeading>
			{equipmentList.length > 0 && (
				<StyledTable caption="Editable Equipment List">
					<PTableHead>
						<PTableHeadRow>
							<PTableHeadCell>
								<FormattedMessage {...editableEquipmentListMessages.equipmentName} />
							</PTableHeadCell>
							<PTableHeadCell>
								<FormattedMessage {...editableEquipmentListMessages.equipmentCode} />
							</PTableHeadCell>
							<PTableHeadCell>
								<FormattedMessage {...editableEquipmentListMessages.equipmentCategory} />
							</PTableHeadCell>
							<PTableHeadCell />
						</PTableHeadRow>
					</PTableHead>
					<PTableBody>
						{equipmentList.map((equipment, index) => (
							<PTableRow key={index}>
								<PTableCell multiline>
									<EquipmentNameWrapper>
										{!equipment.visibleForCustomers && (
											<PTag variant="info-frosted">
												<FormattedMessage
													{...editableEquipmentListMessages.onlyVisibleInternally}
												/>
											</PTag>
										)}
										{equipment.name}
									</EquipmentNameWrapper>
								</PTableCell>
								<PTableCell>
									<PText color="contrast-medium">{equipment.optionCode}</PText>
								</PTableCell>
								<PTableCell>
									<PText color="contrast-medium">{equipment.category}</PText>
								</PTableCell>
								<AlignRight as={PTableCell}>
									<PButtonPure
										type="button"
										icon="delete"
										hideLabel
										onClick={() => {
											setOptionRemovalInProgress((current) => ({
												...current,
												[equipment.name]: true,
											}))
											onRemove(equipment)
											setOptionRemovalInProgress((current) => ({
												...current,
												[equipment.name]: false,
											}))
										}}
										loading={optionRemovalInProgress[equipment.name]}
									/>
								</AlignRight>
							</PTableRow>
						))}
					</PTableBody>
				</StyledTable>
			)}
			<ButtonGroup withoutMarginTop>
				<PButtonPure type="button" icon="plus" onClick={onAddCustomEquipment}>
					<FormattedMessage {...editableEquipmentListMessages.addCustomEquipment} />
				</PButtonPure>
				{onResetToFactoryEquipment && (
					<PButtonPure type="button" icon="duration" onClick={onResetToFactoryEquipment}>
						<FormattedMessage {...editableEquipmentListMessages.resetToFactoryEquipment} />
					</PButtonPure>
				)}
			</ButtonGroup>
		</div>
	)
}

const StyledHeading = styled(PHeading)`
	margin: 0 0 ${spacingStaticSm};
`

const EquipmentNameWrapper = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	column-gap: ${spacingStaticMd};
`

const StyledTable = styled(PTable)`
	margin-bottom: ${spacingStaticMd};
`

const AlignRight = styled.span`
	text-align: end;
`
